import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  MapPin, Plus, Trash2, Edit, Save, X, Image as ImageIcon,
  Waves, Fish, Loader2, ChevronDown, ChevronUp, Link, Unlink
} from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const DIFFICULTY_OPTIONS = ["Beginner", "Intermediate", "Advanced", "Expert"];
const COMMON_MARINE_LIFE = [
  "Manta Rays", "Whale Sharks", "Sea Turtles", "Reef Sharks", "Dolphins",
  "Moray Eels", "Octopus", "Nudibranchs", "Clownfish", "Barracuda",
  "Napoleon Wrasse", "Giant Trevally", "Mola Mola", "Seahorses", "Cuttlefish"
];

const DiveSitesManagement = () => {
  const [diveSites, setDiveSites] = useState([]);
  const [diveCenters, setDiveCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [expandedSite, setExpandedSite] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [locations, setLocations] = useState([]);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    coordinates: { lat: 0, lng: 0 },
    depth: "",
    difficulty: "",
    marine_life: [],
    images: [],
    location: "",
    location_id: "",
    dive_center_id: "",
    is_standalone: true
  });

  const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");

  useEffect(() => {
    fetchDiveSites();
    fetchDiveCenters();
    fetchLocations();
  }, []);

  const fetchDiveSites = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/map/dive-sites`);
      setDiveSites(response.data || []);
    } catch (err) {
      toast.error("Failed to load dive sites");
    } finally {
      setLoading(false);
    }
  };

  const fetchDiveCenters = async () => {
    try {
      const response = await axios.get(`${API}/map/dive-centers`);
      setDiveCenters(response.data || []);
    } catch (err) {
      console.error("Failed to load dive centers");
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data || []);
    } catch (err) {
      console.error("Failed to load locations");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      coordinates: { lat: 0, lng: 0 },
      depth: "",
      difficulty: "",
      marine_life: [],
      images: [],
      location: "",
      location_id: "",
      dive_center_id: "",
      is_standalone: true
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (site) => {
    setFormData({
      name: site.name || "",
      description: site.description || "",
      coordinates: site.coordinates || { lat: 0, lng: 0 },
      depth: site.depth || "",
      difficulty: site.difficulty || "",
      marine_life: site.marine_life || [],
      images: site.images || [],
      location: site.location || "",
      location_id: site.location_id || "",
      dive_center_id: site.dive_center_id || "",
      is_standalone: site.is_standalone || (!site.location_id && !site.dive_center_id)
    });
    setEditingId(site.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        location_id: formData.is_standalone ? null : (formData.location_id || null),
        dive_center_id: formData.is_standalone ? null : (formData.dive_center_id || null),
        is_standalone: formData.is_standalone
      };

      if (editingId) {
        await axios.put(`${API}/map/dive-sites/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Dive site updated");
      } else {
        await axios.post(`${API}/map/dive-sites`, payload, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Dive site created");
      }

      resetForm();
      fetchDiveSites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save dive site");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this dive site?")) return;

    try {
      await axios.delete(`${API}/map/dive-sites/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Dive site deleted");
      fetchDiveSites();
    } catch (err) {
      toast.error("Failed to delete dive site");
    }
  };

  // Quick link/unlink functions
  const handleQuickLink = async (siteId, centerId) => {
    try {
      // Find the center to get its location_id
      const center = diveCenters.find(c => c.id === centerId);
      await axios.put(`${API}/map/dive-sites/${siteId}`, {
        dive_center_id: centerId,
        location_id: center?.location_id || null,
        is_standalone: false
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Dive site linked to center");
      setLinkingId(null);
      fetchDiveSites();
    } catch (err) {
      toast.error("Failed to link dive site");
    }
  };

  const handleUnlink = async (siteId) => {
    if (!window.confirm("Unlink this dive site from its center? It will become a standalone site.")) return;
    
    try {
      await axios.put(`${API}/map/dive-sites/${siteId}`, {
        dive_center_id: null,
        is_standalone: true
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Dive site is now standalone");
      fetchDiveSites();
    } catch (err) {
      toast.error("Failed to unlink dive site");
    }
  };

  const toggleMarineLife = (species) => {
    setFormData(prev => ({
      ...prev,
      marine_life: prev.marine_life.includes(species)
        ? prev.marine_life.filter(s => s !== species)
        : [...prev.marine_life, species]
    }));
  };

  const filteredSites = diveSites.filter(site => {
    if (filterType === "standalone") return site.is_standalone || (!site.location_id && !site.dive_center_id);
    if (filterType === "linked") return (site.location_id || site.dive_center_id) && !site.is_standalone;
    return true;
  });

  const getDiveCenterName = (id) => {
    const center = diveCenters.find(c => c.id === id);
    return center?.name || "Unknown";
  };

  const getLocationName = (id) => {
    const loc = locations.find(l => l.id === id);
    return loc ? `${loc.name}, ${loc.country}` : null;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dive Sites Management</h2>
          <p className="text-gray-600">Manage standalone dive sites and those linked to dive centers</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-cyan-500 hover:bg-cyan-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Dive Site
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: "all", label: "All Sites", count: diveSites.length },
          { key: "standalone", label: "Standalone", count: diveSites.filter(s => s.is_standalone || !s.dive_center_id).length },
          { key: "linked", label: "Linked to Center", count: diveSites.filter(s => s.dive_center_id && !s.is_standalone).length }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilterType(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterType === tab.key
                ? "bg-cyan-500 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={resetForm}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-bold text-gray-900">{editingId ? "Edit Dive Site" : "Add New Dive Site"}</h3>
              <button onClick={resetForm} className="p-1 hover:bg-gray-100 rounded-full"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Site Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Manta Point"
                    required
                  />
                </div>
                
                <div>
                  <Label>Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g., Nusa Penida, Bali"
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the dive site..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Latitude</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.coordinates.lat}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Allow empty, minus sign, or valid number patterns
                      if (val === '' || val === '-' || val === '.' || val === '-.') {
                        setFormData(prev => ({
                          ...prev,
                          coordinates: { ...prev.coordinates, lat: val }
                        }));
                      } else if (!isNaN(parseFloat(val))) {
                        setFormData(prev => ({
                          ...prev,
                          coordinates: { ...prev.coordinates, lat: val }
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      const num = parseFloat(e.target.value);
                      if (!isNaN(num)) {
                        setFormData(prev => ({
                          ...prev,
                          coordinates: { ...prev.coordinates, lat: num }
                        }));
                      }
                    }}
                    placeholder="-8.7267"
                  />
                </div>
                <div>
                  <Label>Longitude</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.coordinates.lng}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Allow empty, minus sign, or valid number patterns
                      if (val === '' || val === '-' || val === '.' || val === '-.') {
                        setFormData(prev => ({
                          ...prev,
                          coordinates: { ...prev.coordinates, lng: val }
                        }));
                      } else if (!isNaN(parseFloat(val))) {
                        setFormData(prev => ({
                          ...prev,
                          coordinates: { ...prev.coordinates, lng: val }
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      const num = parseFloat(e.target.value);
                      if (!isNaN(num)) {
                        setFormData(prev => ({
                          ...prev,
                          coordinates: { ...prev.coordinates, lng: num }
                        }));
                      }
                    }}
                    placeholder="115.1667"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Depth</Label>
                  <Input
                    value={formData.depth}
                    onChange={(e) => setFormData(prev => ({ ...prev, depth: e.target.value }))}
                    placeholder="e.g., 5-30m"
                  />
                </div>
                <div>
                  <Label>Difficulty</Label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="">Select difficulty</option>
                    {DIFFICULTY_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Standalone Toggle */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_standalone"
                    checked={formData.is_standalone}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      is_standalone: e.target.checked,
                      dive_center_id: e.target.checked ? "" : prev.dive_center_id
                    }))}
                    className="w-4 h-4 text-cyan-500 rounded"
                  />
                  <Label htmlFor="is_standalone" className="cursor-pointer">
                    Standalone dive site (not linked to any location)
                  </Label>
                </div>
                
                {!formData.is_standalone && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <Label>Assign to Location (primary)</Label>
                      <select
                        value={formData.location_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, location_id: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
                      >
                        <option value="">Select location</option>
                        {locations.map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name}, {loc.country}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">All dive centers in this location will see this site</p>
                    </div>
                    <div>
                      <Label>Operated by (optional)</Label>
                      <select
                        value={formData.dive_center_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, dive_center_id: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500"
                      >
                        <option value="">No specific center</option>
                        {diveCenters
                          .filter(c => !formData.location_id || c.location_id === formData.location_id)
                          .map(center => (
                            <option key={center.id} value={center.id}>{center.name}</option>
                          ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Marine Life */}
              <div>
                <Label className="flex items-center gap-2">
                  <Fish className="w-4 h-4" />
                  Marine Life
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {COMMON_MARINE_LIFE.map(species => (
                    <button
                      key={species}
                      type="button"
                      onClick={() => toggleMarineLife(species)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        formData.marine_life.includes(species)
                          ? "bg-cyan-500 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {species}
                    </button>
                  ))}
                </div>
                <Input
                  className="mt-2"
                  placeholder="Add custom species (comma separated)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const species = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                      setFormData(prev => ({
                        ...prev,
                        marine_life: [...new Set([...prev.marine_life, ...species])]
                      }));
                      e.target.value = '';
                    }
                  }}
                />
              </div>

              {/* Images */}
              <div>
                <Label className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  Images
                </Label>
                <div className="mt-2">
                  <ImageUpload
                    uploadType="forum"
                    multiple={true}
                    currentImages={formData.images}
                    onImagesUpdated={(images) => setFormData(prev => ({ ...prev, images }))}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600" disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingId ? "Update" : "Create"}
                </Button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Sites List */}
      <div className="space-y-3">
        {filteredSites.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No dive sites found</p>
            </CardContent>
          </Card>
        ) : (
          filteredSites.map(site => (
            <Card key={site.id} className="overflow-hidden">
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedSite(expandedSite === site.id ? null : site.id)}
              >
                <div className="flex items-center gap-4">
                  {site.images?.[0] ? (
                    <img 
                      src={site.images[0].startsWith('/api') ? `${BACKEND_URL}${site.images[0]}` : site.images[0]}
                      alt={site.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                      <Waves className="w-8 h-8 text-white" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900">{site.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <MapPin className="w-3 h-3" />
                      {site.location || `${site.coordinates?.lat?.toFixed(4)}, ${site.coordinates?.lng?.toFixed(4)}`}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {site.is_standalone || (!site.location_id && !site.dive_center_id) ? (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          Standalone
                        </span>
                      ) : (
                        <>
                          {site.location_id && (
                            <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs rounded-full">
                              {getLocationName(site.location_id) || 'Unknown location'}
                            </span>
                          )}
                          {site.dive_center_id && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                              {getDiveCenterName(site.dive_center_id)}
                            </span>
                          )}
                        </>
                      )}
                      {site.difficulty && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                          {site.difficulty}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Link/Unlink Button */}
                  {site.is_standalone || (!site.location_id && !site.dive_center_id) ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      onClick={(e) => { e.stopPropagation(); setLinkingId(site.id); }}
                      title="Link to location"
                    >
                      <Link className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      onClick={(e) => { e.stopPropagation(); handleUnlink(site.id); }}
                      title="Unlink from dive center"
                    >
                      <Unlink className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); handleEdit(site); }}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={(e) => { e.stopPropagation(); handleDelete(site.id); }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {expandedSite === site.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
              
              {expandedSite === site.id && (
                <div className="px-4 pb-4 border-t bg-gray-50">
                  <div className="pt-4 space-y-3">
                    {site.description && (
                      <p className="text-gray-600">{site.description}</p>
                    )}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {site.depth && (
                        <div>
                          <span className="text-gray-500">Depth:</span> {site.depth}
                        </div>
                      )}
                      {site.rating && (
                        <div>
                          <span className="text-gray-500">Rating:</span> {site.rating}/5
                        </div>
                      )}
                    </div>
                    {site.marine_life?.length > 0 && (
                      <div>
                        <span className="text-sm text-gray-500">Marine Life:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {site.marine_life.map(species => (
                            <span key={species} className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs rounded-full">
                              {species}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {site.images?.length > 1 && (
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {site.images.map((img, i) => (
                          <img 
                            key={i}
                            src={img.startsWith('/api') ? `${BACKEND_URL}${img}` : img}
                            alt=""
                            className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Quick Link Modal */}
      {linkingId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link className="w-5 h-5 text-blue-500" />
                Link to Dive Center
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Select a dive center to link "{diveSites.find(s => s.id === linkingId)?.name}" to.
                The site will be assigned to the center's location.
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {diveCenters.map(center => {
                  const loc = locations.find(l => l.id === center.location_id);
                  return (
                    <button
                      key={center.id}
                      onClick={() => handleQuickLink(linkingId, center.id)}
                      className="w-full p-3 text-left rounded-lg border hover:bg-cyan-50 hover:border-cyan-500 transition-colors"
                    >
                      <p className="font-medium text-gray-900">{center.name}</p>
                      {loc && (
                        <p className="text-sm text-gray-500">{loc.name}, {loc.country}</p>
                      )}
                      {!loc && center.location && (
                        <p className="text-sm text-gray-500">{center.location}</p>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={() => setLinkingId(null)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DiveSitesManagement;
